import { Button } from '@design-system';
import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useState } from 'react';

import { getProductStatus } from '../../../helpers/purchasing';
import { useHubspotChat } from '../../../hooks/useHubspotChat';
import { ProductStatus } from '../../../types/Analytics';
import {
  ProductPurchaseDetails,
  ProductPurchasingController,
} from '../../../types/Purchasing';
import { ButtonLocation } from '../../../types/Subscriptions';
import { NFTProductType } from '../../../types/web3/product';
import { TrackEnquiryClicked } from '../../../utils/analytics';
import { storyblokProductReleaseType } from '../../../utils/storyblokReleaseType';
import { SimpleDialog } from '../../Dialog/SimpleDialog';
import { FewLeft } from './FewLeft';

type Props<T extends ProductPurchaseDetails> = {
  purchasingController?: ProductPurchasingController<T>;
  product: ProductType | NFTProductType;
  analytics?: Analytics;
  buttonLocation?: ButtonLocation;
};

export const EnquireButton: React.FC<Props<ProductPurchaseDetails>> = ({
  analytics,
  buttonLocation = 'product page',
  product,
  purchasingController,
}) => {
  const { t } = useTranslation('product');
  const { openHandler } = useHubspotChat();
  const [showDialog, setShowDialog] = useState(false);

  const productId = !('tokenId' in product) ? product.id[0]?.id : '';

  const [emitter] = useAnalyticsEmitter();

  const handleHowToClick = useCallback(() => {
    emitter('', 'how clicked', {
      type: 'private',
    });
  }, [, emitter]);

  return (
    <div className="flex w-full flex-col items-center gap-y-3">
      <FewLeft
        className="mb-3"
        tags={purchasingController?.product?.tags || []}
      />
      <Button
        fullWidth
        label={t('subscriptionCta.private.buttonEnquireTitle')}
        onClick={() => {
          const isNFTProduct = 'tokenId' in product;
          const artist = product.productArtist?.[0] as ResolvedRelatedArtist;
          const productStatus: ProductStatus = !isNFTProduct
            ? getProductStatus(product, purchasingController)
            : 'unavailable';

          analytics &&
            purchasingController?.product &&
            TrackEnquiryClicked({
              page: analytics.page,
              context: analytics.context,
              artistName: artist.artist.name,
              buttonLocation,
              productId,
              productReleaseType: storyblokProductReleaseType(product),
              productStatus,
              productTitle: product.title,
              productPricesAndCurrencies: !isNFTProduct
                ? product.pricesAndAvailability?.pricesAndCurrencies
                : undefined,
              productPrice: purchasingController?.product.price,
              productCurrencyCode: purchasingController?.product.currency,
              type: 'clicked',
            });

          openHandler();
        }}
        variant="primary"
      />
      <button
        className="hyperlink"
        onClick={() => {
          setShowDialog(true);
          handleHowToClick();
        }}
        type="button"
      >
        {t('subscriptionCta.private.learnMoreDialog.title')}
      </button>
      <SimpleDialog
        onClose={() => setShowDialog(false)}
        open={showDialog}
        title={t('subscriptionCta.private.learnMoreDialog.title')}
      >
        <p>{t('subscriptionCta.private.learnMoreDialog.paragraph')}</p>
      </SimpleDialog>
    </div>
  );
};
